import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  CheckCircle, Phone, Mail, Calendar, FileText, Building2, Menu, X, 
  Calculator, PenTool, Briefcase, Clock, Shield, TrendingUp, Star,
  ArrowRight, Check, FileCheck, Users, DollarSign, Receipt, Landmark,
  ClipboardList, UserCheck, Wallet, Download, ExternalLink, Facebook,
  Instagram, Twitter
} from "lucide-react";
import Marquee from "react-fast-marquee";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tax deadline countdown helper
const calculateTimeLeft = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  // Use current year's April 15, or next year if already passed
  let taxDeadline = new Date(`${currentYear}-04-15T23:59:59`);
  if (now > taxDeadline) {
    taxDeadline = new Date(`${currentYear + 1}-04-15T23:59:59`);
  }
  const difference = taxDeadline - now;
  
  if (difference > 0) {
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }
  return { days: 0, hours: 0, minutes: 0, seconds: 0 };
};

const Home = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "tax_prep",
    filing_method: "in_office",
    preferred_date: "",
    preferred_time: "",
    how_heard: "",
    message: ""
  });

  const [contactData, setContactData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const [submitting, setSubmitting] = useState(false);

  const allTimeSlots = [
    "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
    "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM"
  ];

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Chivo:wght@400;700;900&family=Manrope:wght@400;500;700&family=Oswald:wght@500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch booked slots when date changes
  const fetchBookedSlots = async (date) => {
    if (!date) {
      setBookedSlots([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/appointments/booked-slots/${date}`);
      setBookedSlots(response.data.booked_slots || []);
    } catch (error) {
      console.error("Error fetching booked slots:", error);
      setBookedSlots([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // If date changed, fetch booked slots and reset time selection
    if (name === "preferred_date") {
      fetchBookedSlots(value);
      setFormData(prev => ({ ...prev, preferred_date: value, preferred_time: "" }));
    }
  };

  const handleContactChange = (e) => {
    setContactData({
      ...contactData,
      [e.target.name]: e.target.value
    });
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/appointments`, formData);
      toast.success("🎉 Appointment booked! Check your email for confirmation.", {
        duration: 6000
      });
      toast("📧 Don't see the email? Check your spam folder and add bookings@fastcashtaxsvcs.com to your contacts.", {
        duration: 8000,
        icon: "💡"
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "tax_prep",
        filing_method: "in_office",
        preferred_date: "",
        preferred_time: "",
        how_heard: "",
        message: ""
      });
      setBookedSlots([]);
    } catch (error) {
      toast.error("Failed to book appointment. Please try calling us.");
      console.error("Booking error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, contactData);
      toast.success("Message sent! We'll get back to you soon.");
      setContactData({
        name: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try calling us.");
      console.error("Contact error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setShowMobileMenu(false);
    }
  };

  const documents = [
    {
      title: "Client Tax Portal",
      url: "https://www.taxpassapp.com/?taxpassid=D8XWNK5W#/login",
      icon: <Calculator size={24} />,
      description: "Access your tax documents"
    },
    {
      title: "Client Checklist",
      url: "https://na1.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhDEHYxR2-RKb0bcJSXkYCZ1M4W9c7vIbmv4DGrDrQryIh7WNUZTOXGXqkwBvOpkMyo*",
      icon: <ClipboardList size={24} />,
      description: "Tax preparation checklist"
    },
    {
      title: "Business Expense Form",
      url: "https://na1.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhCcIgcFKWiXpq8MRa2oK8l9ltAyKItlHBU8LZkwB3pTKQ-iH_00vrSqVh4NHLKIoyI*",
      icon: <Receipt size={24} />,
      description: "Track business expenses"
    },
    {
      title: "Rental Property Expense",
      url: "https://na1.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhCNVIw1lt2EBU_atjHqamQlGq5_LR5ezghSdvZydC3MKF99YqR5gZvul49pGiTUR60*",
      icon: <Building2 size={24} />,
      description: "Rental property tracking"
    },
    {
      title: "Trucker Expense Form",
      url: "https://na1.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhCEcU0skG0X30PvfKdtC4erP_GNGOi7EIVlTr7SovWWI5KPS1ZsvyjNACt9p_jnEOo*",
      icon: <DollarSign size={24} />,
      description: "Truck driver expense tracking"
    }
  ];

  const allServices = [
    {
      category: "Tax Services",
      icon: <Calculator size={32} />,
      services: [
        "Individual & Family Tax Returns",
        "Self-Employed / 1099 / Gig Workers",
        "Small Business Tax Preparation",
        "Corporation Taxes (C Corp & S Corp)",
        "Non-Profit Tax Filing",
        "Trust Tax Returns",
        "Tax Planning & Consultation"
      ]
    },
    {
      category: "Business Services",
      icon: <Briefcase size={32} />,
      services: [
        "LLC Formation & Filing",
        "DBA Registration",
        "Corporation Setup (C Corp & S Corp)",
        "Non-Profit Organization Filing",
        "Business Funding Assistance",
        "Operating Agreements",
        "Business Document Preparation"
      ]
    },
    {
      category: "Payroll & HR Services",
      icon: <Users size={32} />,
      services: [
        "Bookkeeping Services",
        "Payroll Processing",
        "W-2 Preparation & Filing",
        "1099 Preparation & Filing",
        "Payroll Tax Filing & Compliance",
        "HR Document Preparation",
        "Employee Records Management"
      ]
    },
    {
      category: "Notary & Legal Documents",
      icon: <PenTool size={32} />,
      services: [
        "Notary Public Services",
        "Title Transfers",
        "Document Authentication",
        "Legal Document Preparation",
        "Real Estate Document Notarization"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Toaster position="top-center" richColors />
      
      {/* Top Trust Bar */}
      <div className="bg-[#141713] text-white overflow-hidden" data-testid="trust-marquee">
        <Marquee speed={50} gradient={false}>
          <div className="flex items-center gap-12 py-3 px-6 text-sm font-bold tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <span>IRS AUTHORIZED E-FILE PROVIDER</span>
            <span className="text-[#9CBA76]">•</span>
            <span>LICENSED NOTARY PUBLIC</span>
            <span className="text-[#9CBA76]">•</span>
            <span>24-HOUR TURNAROUND AVAILABLE</span>
            <span className="text-[#9CBA76]">•</span>
            <span>CORPORATE TAX SPECIALIST</span>
            <span className="text-[#9CBA76]">•</span>
            <span>PAYROLL SERVICES</span>
            <span className="text-[#9CBA76]">•</span>
            <span>FREE CONSULTATION</span>
            <span className="text-[#9CBA76]">•</span>
          </div>
        </Marquee>
      </div>

      {/* Header */}
      <header 
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/95 border-b border-slate-700/50 shadow-sm"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between py-10 md:py-12">
            <a href="#" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_taxbizpro/artifacts/dif01lnb_BLACKFASTCASHLOGO.jpeg" 
                alt="Fast Cash Tax Services – Taxes, Notary, Business Formations" 
                style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
                data-testid="header-logo"
              />
            </a>

            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              <a 
                href="#how" 
                onClick={(e) => { e.preventDefault(); scrollToSection("how"); }}
                className="text-sm font-bold text-white hover:text-[#CB141F] transition-colors"
                data-testid="nav-how-link"
              >
                How It Works
              </a>
              <a 
                href="#services" 
                onClick={(e) => { e.preventDefault(); scrollToSection("services"); }}
                className="text-sm font-bold text-white hover:text-[#CB141F] transition-colors"
                data-testid="nav-services-link"
              >
                Services
              </a>
              <a 
                href="#documents" 
                onClick={(e) => { e.preventDefault(); scrollToSection("documents"); }}
                className="text-sm font-bold text-white hover:text-[#CB141F] transition-colors"
                data-testid="nav-documents-link"
              >
                Documents
              </a>
              <a 
                href="#about" 
                onClick={(e) => { e.preventDefault(); scrollToSection("about"); }}
                className="text-sm font-bold text-white hover:text-[#CB141F] transition-colors"
                data-testid="nav-about-link"
              >
                About
              </a>
              <a 
                href="tel:+17132871040"
                className="text-sm font-bold text-white hover:text-[#CB141F] transition-colors flex items-center gap-2"
                data-testid="nav-phone-link"
              >
                <Phone size={16} />
                (713) 287-1040
              </a>
              <button 
                onClick={() => scrollToSection("book")}
                className="bg-[#CB141F] text-white px-8 py-3 rounded-full font-bold transition-all hover:bg-[#A00F18] active:scale-95 shadow-lg hover:shadow-xl"
                data-testid="nav-book-button"
              >
                Book Now
              </button>
            </nav>

            <button 
              className="lg:hidden text-slate-700 p-2"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {showMobileMenu && (
            <div className="lg:hidden py-6 space-y-4 border-t border-slate-700" data-testid="mobile-nav">
              <a href="#how" onClick={(e) => { e.preventDefault(); scrollToSection("how"); }} className="block py-2 font-bold text-white">How It Works</a>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection("services"); }} className="block py-2 font-bold text-white">Services</a>
              <a href="#documents" onClick={(e) => { e.preventDefault(); scrollToSection("documents"); }} className="block py-2 font-bold text-white">Documents</a>
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection("about"); }} className="block py-2 font-bold text-white">About</a>
              <a href="tel:+17132871040" className="block py-2 font-bold text-white">Call (713) 287-1040</a>
              <button 
                onClick={() => scrollToSection("book")}
                className="w-full bg-[#CB141F] text-white px-8 py-3 rounded-full font-bold"
                data-testid="mobile-nav-book-button"
              >
                Book Now
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Referral Banner */}
      <div className="bg-gradient-to-r from-[#365924] via-[#44762F] to-[#365924] py-4 px-6" data-testid="referral-banner">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">💰</span>
            <div>
              <p className="text-white font-black text-lg md:text-xl tracking-wide">
                REFER A FRIEND & RECEIVE <span className="text-yellow-300 text-2xl md:text-3xl">$75.00</span>
              </p>
            </div>
            <span className="text-3xl hidden md:block">💰</span>
          </div>
          <button 
            onClick={() => scrollToSection("book")}
            className="bg-white text-[#44762F] px-6 py-2 rounded-full font-bold text-sm hover:bg-yellow-300 transition-all hover:scale-105 shadow-lg"
            data-testid="referral-book-button"
          >
            Book Now
          </button>
        </div>
        <p className="text-center text-white/80 text-xs mt-2">
          *Restrictions apply: Client must file return and return must be fully funded to receive referral fee.
        </p>
      </div>

      {/* Tax Deadline Countdown */}
      <div className="bg-black py-6 px-6 border-y border-yellow-500/30" data-testid="countdown-banner">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-yellow-400 font-bold text-sm md:text-base mb-3 tracking-wider">
            ⚠️ TAX FILING DEADLINE - APRIL 15, 2026 ⚠️
          </p>
          <div className="flex justify-center gap-3 md:gap-6">
            <div className="bg-gradient-to-b from-[#CB141F] to-[#8B0000] rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[90px] shadow-lg">
              <p className="text-white text-2xl md:text-4xl font-black">{timeLeft.days}</p>
              <p className="text-white/80 text-xs md:text-sm font-medium">DAYS</p>
            </div>
            <div className="bg-gradient-to-b from-[#CB141F] to-[#8B0000] rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[90px] shadow-lg">
              <p className="text-white text-2xl md:text-4xl font-black">{timeLeft.hours}</p>
              <p className="text-white/80 text-xs md:text-sm font-medium">HOURS</p>
            </div>
            <div className="bg-gradient-to-b from-[#CB141F] to-[#8B0000] rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[90px] shadow-lg">
              <p className="text-white text-2xl md:text-4xl font-black">{timeLeft.minutes}</p>
              <p className="text-white/80 text-xs md:text-sm font-medium">MINS</p>
            </div>
            <div className="bg-gradient-to-b from-[#CB141F] to-[#8B0000] rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[90px] shadow-lg">
              <p className="text-white text-2xl md:text-4xl font-black">{timeLeft.seconds}</p>
              <p className="text-white/80 text-xs md:text-sm font-medium">SECS</p>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-4">
            Don't wait until the last minute! <button onClick={() => scrollToSection("book")} className="text-[#CB141F] font-bold hover:underline">Book your appointment today →</button>
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-b from-black via-[#1a0000] to-black" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 
                className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] text-white"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="hero-title"
              >
                MAX REFUND.<br />
                MINIMUM<br />
                <span className="text-[#CB141F]">STRESS.</span>
              </h1>
              
              <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-xl" data-testid="hero-description">
                Full-service tax preparation, payroll, bookkeeping, and business formation. 
                <strong> In-office, drop-off, or virtual</strong>—whatever fits your life.
              </p>

              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection("book")}
                  className="bg-[#CB141F] text-white px-10 py-4 rounded-full font-bold transition-all hover:bg-[#A00F18] active:scale-95 shadow-lg hover:shadow-xl flex items-center gap-2"
                  data-testid="hero-book-button"
                >
                  Book Appointment <ArrowRight size={20} />
                </button>
                <a 
                  href="tel:+17132871040"
                  className="bg-white text-[#141713] border-2 border-[#141713] px-10 py-4 rounded-full font-bold transition-all hover:bg-slate-50 active:scale-95 shadow-lg flex items-center gap-2"
                  data-testid="hero-call-button"
                >
                  <Phone size={20} /> Call Now
                </a>
              </div>

              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center">
                    <Check className="text-[#44762F]" size={20} />
                  </div>
                  <span className="font-bold text-white">E-File Ready</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center">
                    <Shield className="text-[#44762F]" size={20} />
                  </div>
                  <span className="font-bold text-white">Secure & Private</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center">
                    <Clock className="text-[#44762F]" size={20} />
                  </div>
                  <span className="font-bold text-white">Fast Turnaround</span>
                </div>
              </div>
            </div>

            <div className="relative" data-testid="hero-visual">
              <img 
                src="https://images.pexels.com/photos/7821915/pexels-photo-7821915.jpeg"
                alt="Happy client meeting"
                className="rounded-2xl shadow-2xl w-full object-cover h-[500px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border-2 border-[#44762F] p-6 rounded-2xl shadow-xl">
                <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Avg. Refund</div>
                <div className="text-4xl font-black text-[#44762F]" style={{ fontFamily: 'Chivo, sans-serif' }}>$3,450</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <TrendingUp size={14} /> +15% vs last year
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to File */}
      <section id="how" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Choose Your Filing Method
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Pick the option that works best for you. We make it simple, secure, and fast.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm group" data-testid="filing-option-in-office">
              <div className="w-14 h-14 rounded-xl bg-[#CB141F]/10 flex items-center justify-center mb-4 group-hover:bg-[#CB141F] transition-colors">
                <Building2 className="text-[#CB141F] group-hover:text-white transition-colors" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#141713] mb-2">In-Office</h3>
              <p className="text-slate-600 mb-3">Meet with a tax pro and file confidently.</p>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Best for complex situations</span>
            </div>

            <div className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm group" data-testid="filing-option-dropoff">
              <div className="w-14 h-14 rounded-xl bg-[#CB141F]/10 flex items-center justify-center mb-4 group-hover:bg-[#CB141F] transition-colors">
                <FileText className="text-[#CB141F] group-hover:text-white transition-colors" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#141713] mb-2">Drop-Off</h3>
              <p className="text-slate-600 mb-3">Leave your documents. We'll call you when ready.</p>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Fast + convenient</span>
            </div>

            <div className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm group" data-testid="filing-option-virtual">
              <div className="w-14 h-14 rounded-xl bg-[#CB141F]/10 flex items-center justify-center mb-4 group-hover:bg-[#CB141F] transition-colors">
                <Mail className="text-[#CB141F] group-hover:text-white transition-colors" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#141713] mb-2">Virtual</h3>
              <p className="text-slate-600 mb-3">Complete intake online. Phone/video review available.</p>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">No in-person required</span>
            </div>

            <div className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm group" data-testid="filing-option-returning">
              <div className="w-14 h-14 rounded-xl bg-[#44762F]/10 flex items-center justify-center mb-4 group-hover:bg-[#44762F] transition-colors">
                <Star className="text-[#44762F] group-hover:text-white transition-colors" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#141713] mb-2">Returning Client</h3>
              <p className="text-slate-600 mb-3">Quick updates and streamlined filing.</p>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Bring last year's return</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 bg-black" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Complete Financial Services
            </h2>
            <p className="text-lg text-slate-300">
              From taxes to payroll, we've got your business covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {allServices.map((category, idx) => (
              <div 
                key={idx}
                className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm"
                data-testid={`service-category-${idx}`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#CB141F]/10 flex items-center justify-center flex-shrink-0">
                    <div className="text-[#CB141F]">{category.icon}</div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#141713] mb-2">{category.category}</h3>
                  </div>
                </div>
                <ul className="space-y-2">
                  {category.services.map((service, serviceIdx) => (
                    <li key={serviceIdx} className="flex items-start gap-2 text-slate-600">
                      <Check className="text-[#44762F] flex-shrink-0 mt-1" size={18} />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button 
              onClick={() => scrollToSection("book")}
              className="bg-[#CB141F] text-white px-10 py-4 rounded-full font-bold transition-all hover:bg-[#A00F18] active:scale-95 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
              data-testid="services-cta"
            >
              Schedule Consultation <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="documents-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Quick Access Documents
            </h2>
            <p className="text-lg text-slate-300">
              Access your portal and download essential forms
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {documents.map((doc, idx) => (
              <a
                key={idx}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-slate-200 p-6 rounded-2xl hover:border-[#CB141F] hover:shadow-xl transition-all shadow-sm group"
                data-testid={`document-link-${idx}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#CB141F]/10 flex items-center justify-center mb-4 group-hover:bg-[#CB141F] transition-colors">
                  <div className="text-[#CB141F] group-hover:text-white transition-colors">{doc.icon}</div>
                </div>
                <h4 className="font-bold text-[#141713] mb-2 flex items-center justify-between">
                  {doc.title}
                  <ExternalLink size={16} className="text-slate-400" />
                </h4>
                <p className="text-sm text-slate-600">{doc.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Office Background */}
      <section id="about" className="py-24 md:py-32 bg-black relative overflow-hidden" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              {/* Professional Photo with Real Office Background */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
                <img 
                  src="https://customer-assets.emergentagent.com/job_taxbizpro/artifacts/n40ztsd0_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2007_57_28%20PM.png" 
                  alt="Tonya Patterson-Walker - CEO" 
                  className="w-full h-auto object-cover"
                  data-testid="professional-photo"
                />
                <div className="absolute bottom-6 left-6 bg-[#CB141F] text-white px-6 py-4 rounded-xl shadow-xl z-20">
                  <div className="text-xs font-bold uppercase tracking-wider mb-1">CEO</div>
                  <div className="text-xl font-black">Tonya Patterson-Walker</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Professional Tax Services You Can Trust
              </h2>
              <p className="text-lg leading-relaxed text-slate-300">
                At Fast Cash Tax Services, we understand that managing taxes, payroll, and business finances can be overwhelming. 
                That's why we're committed to making the process as simple and straightforward as possible.
              </p>
              <p className="text-lg leading-relaxed text-slate-300">
                With years of experience serving individuals and businesses across Houston, we provide accurate, 
                professional financial services with a personal touch. From individual tax returns to corporate filings, 
                payroll processing to business formation—we're your one-stop financial partner.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#44762F]" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-white">Licensed & Certified</div>
                    <div className="text-sm text-slate-400">IRS authorized provider</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#44762F]" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-white">Year-Round Support</div>
                    <div className="text-sm text-slate-400">We're here when you need us</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#44762F]" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-white">Secure & Confidential</div>
                    <div className="text-sm text-slate-400">Your data is protected</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#44762F]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#44762F]" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-white">Full-Service Partner</div>
                    <div className="text-sm text-slate-400">All your financial needs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="book" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="booking-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Book Your Appointment
            </h2>
            <p className="text-lg text-slate-300">
              Complete the form below and we'll contact you to confirm your appointment.
            </p>
          </div>

          <form onSubmit={handleBookingSubmit} className="bg-white border border-slate-200 p-8 md:p-10 rounded-2xl shadow-xl" data-testid="booking-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-[#141713] mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold text-[#141713] mb-2">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-email-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-[#141713] mb-2">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-phone-input"
                />
              </div>

              <div>
                <label htmlFor="preferred_date" className="block text-sm font-bold text-[#141713] mb-2">Preferred Date *</label>
                <input
                  type="date"
                  id="preferred_date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-date-input"
                />
              </div>

              <div>
                <label htmlFor="preferred_time" className="block text-sm font-bold text-[#141713] mb-2">Preferred Time (CST) *</label>
                <select
                  id="preferred_time"
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.preferred_date}
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="booking-time-select"
                >
                  <option value="">{formData.preferred_date ? "Select a time slot" : "Select a date first"}</option>
                  {allTimeSlots.map((time) => {
                    const isBooked = bookedSlots.includes(time);
                    return (
                      <option key={time} value={time} disabled={isBooked}>
                        {time}{isBooked ? " (Booked)" : ""}
                      </option>
                    );
                  })}
                </select>
                {formData.preferred_date && bookedSlots.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">Gray options are already booked</p>
                )}
              </div>

              <div>
                <label htmlFor="service_type" className="block text-sm font-bold text-[#141713] mb-2">Service Type *</label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-service-select"
                >
                  <option value="tax_prep">Tax Preparation</option>
                  <option value="corp_tax">Corporation Taxes</option>
                  <option value="payroll">Payroll Services</option>
                  <option value="bookkeeping">Bookkeeping</option>
                  <option value="notary">Notary</option>
                  <option value="business_formation">Business Formation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="filing_method" className="block text-sm font-bold text-[#141713] mb-2">Appointment Type *</label>
                <select
                  id="filing_method"
                  name="filing_method"
                  value={formData.filing_method}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-method-select"
                >
                  <option value="in_office">In-Office</option>
                  <option value="drop_off">Drop-Off</option>
                  <option value="virtual">Virtual</option>
                </select>
              </div>

              <div>
                <label htmlFor="how_heard" className="block text-sm font-bold text-[#141713] mb-2">How Did You Hear About Us?</label>
                <select
                  id="how_heard"
                  name="how_heard"
                  value={formData.how_heard}
                  onChange={handleInputChange}
                  className="w-full h-12 bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 transition-colors"
                  data-testid="booking-how-heard-select"
                >
                  <option value="">Select an option</option>
                  <option value="referral">Referral from Friend/Family</option>
                  <option value="google">Google Search</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="threads">Threads</option>
                  <option value="twitter">X (Twitter)</option>
                  <option value="drive_by">Drove By / Saw Sign</option>
                  <option value="returning">Returning Client</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="message" className="block text-sm font-bold text-[#141713] mb-2">Additional Information</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="4"
                className="w-full bg-slate-50 border border-slate-200 text-[#141713] focus:border-[#CB141F] focus:ring-0 rounded-lg px-4 py-3 transition-colors resize-none"
                data-testid="booking-message-textarea"
                placeholder="Tell us more about your needs..."
              ></textarea>
            </div>

            <button 
              type="submit" 
              disabled={submitting}
              className="w-full mt-6 bg-[#CB141F] text-white px-10 py-4 rounded-full font-bold transition-all hover:bg-[#A00F18] active:scale-95 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="booking-submit-button"
            >
              {submitting ? "Booking..." : "Book Appointment"}
            </button>

            <p className="text-center text-sm text-slate-400 mt-4">
              Prefer to book by phone? <a href="tel:+17132871040" className="text-[#CB141F] font-bold hover:underline">Call (713) 287-1040</a>
            </p>
          </form>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#141713] text-white" data-testid="contact-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Questions? We're Here to Help.
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Call us today for a free consultation or send us a message.
          </p>
          <div className="flex flex-wrap gap-6 justify-center items-center mb-6">
            <a 
              href="tel:+17132871040"
              className="text-4xl md:text-5xl font-black tracking-tight hover:text-[#9CBA76] transition-colors"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="footer-phone"
            >
              (713) 287-1040
            </a>
          </div>
          <div className="flex flex-wrap gap-4 justify-center items-center text-slate-300">
            <a 
              href="mailto:tax@fastcashtaxsvcs.com"
              className="flex items-center gap-2 hover:text-[#9CBA76] transition-colors"
              data-testid="footer-email"
            >
              <Mail size={20} />
              tax@fastcashtaxsvcs.com
            </a>
            <span className="text-slate-500">•</span>
            <div className="flex items-center gap-2">
              <FileText size={20} />
              Fax: (713) 370-0023
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
            <div>
              <div className="inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_taxbizpro/artifacts/dif01lnb_BLACKFASTCASHLOGO.jpeg" 
                  alt="Fast Cash Tax Services" 
                  style={{ width: '240px', height: 'auto', maxWidth: '100%' }}
                />
              </div>
              <p className="text-slate-300 text-sm mb-4">
                Full-service tax preparation, payroll, bookkeeping, and business services you can trust.
              </p>
              
              {/* Social Media Links */}
              <div className="flex gap-3 mt-4">
                <a 
                  href="https://www.threads.com/@fastcashtaxservices?igshid=NTc4MTIwNjQ2YQ==" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#CB141F] flex items-center justify-center transition-colors p-2"
                  aria-label="Threads"
                  data-testid="social-threads"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/01/Threads_%28app%29.svg/512px-Threads_%28app%29.svg.png" 
                    alt="Threads" 
                    className="w-5 h-5 object-contain invert"
                  />
                </a>
                
                <a 
                  href="https://www.instagram.com/fastcashtaxservices?igsh=cWJqc3VxOXE5YnBq" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#CB141F] flex items-center justify-center transition-colors p-2"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Instagram_logo_2022.svg/512px-Instagram_logo_2022.svg.png" 
                    alt="Instagram" 
                    className="w-5 h-5 object-contain"
                  />
                </a>
                
                <a 
                  href="https://www.facebook.com/fastcashtaxservices?mibextid=LQQJ4d" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#CB141F] flex items-center justify-center transition-colors p-2"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Facebook_f_logo_%282019%29.svg/512px-Facebook_f_logo_%282019%29.svg.png" 
                    alt="Facebook" 
                    className="w-5 h-5 object-contain"
                  />
                </a>
                
                <a 
                  href="https://x.com/fastcashsvcs?s=21" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#CB141F] flex items-center justify-center transition-colors p-2"
                  aria-label="Twitter/X"
                  data-testid="social-twitter"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/X_%28formerly_Twitter%29_logo_late_2025.svg/256px-X_%28formerly_Twitter%29_logo_late_2025.svg.png" 
                    alt="X (Twitter)" 
                    className="w-5 h-5 object-contain invert"
                  />
                </a>
                
                <a 
                  href="https://www.tiktok.com/@fastcashtaxservices?_r=1&_t=ZT-93St2rKly8l" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#CB141F] flex items-center justify-center transition-colors p-2"
                  aria-label="TikTok"
                  data-testid="social-tiktok"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Tiktok_icon.svg/64px-Tiktok_icon.svg.png" 
                    alt="TikTok" 
                    className="w-5 h-5 object-contain"
                  />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-white">Quick Links</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li><a href="#how" onClick={(e) => { e.preventDefault(); scrollToSection("how"); }} className="hover:text-[#CB141F] transition-colors">How It Works</a></li>
                <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection("services"); }} className="hover:text-[#CB141F] transition-colors">Services</a></li>
                <li><a href="#documents" onClick={(e) => { e.preventDefault(); scrollToSection("documents"); }} className="hover:text-[#CB141F] transition-colors">Documents</a></li>
                <li><a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection("about"); }} className="hover:text-[#CB141F] transition-colors">About</a></li>
                <li><a href="#book" onClick={(e) => { e.preventDefault(); scrollToSection("book"); }} className="hover:text-[#CB141F] transition-colors">Book Appointment</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-white">Contact</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-center gap-2">
                  <Phone size={16} />
                  <a href="tel:+17132871040" className="hover:text-[#CB141F] transition-colors">(713) 287-1040</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={16} />
                  <a href="mailto:tax@fastcashtaxsvcs.com" className="hover:text-[#CB141F] transition-colors">tax@fastcashtaxsvcs.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <FileText size={16} />
                  <span>Fax: (713) 370-0023</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-white">Hours of Operation</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span className="text-white font-medium">10am - 7pm</span>
                </li>
                <li className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="text-white font-medium">10am - 5pm</span>
                </li>
                <li className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="text-red-400 font-medium">Closed</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-white">Scan to Book</h3>
              <div className="bg-white p-3 rounded-lg inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_32fd7e9d-bcd1-48c2-bf67-4c1eb8c78298/artifacts/wlsuqjws_adobe-express-qr-code.png" 
                  alt="QR Code - Book Appointment" 
                  className="w-28 h-28"
                  data-testid="qr-code"
                />
              </div>
              <p className="text-slate-400 text-xs mt-2">Scan to visit our website</p>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 text-center text-sm text-slate-300">
            <p>© {new Date().getFullYear()} Fast Cash Tax Services. All rights reserved.</p>
            <p className="mt-2 text-slate-400">Tax Preparation • Payroll Services • Bookkeeping • Business Formation • Notary • Corporate Taxes</p>
            <a 
              href="/admin" 
              className="inline-block mt-4 text-slate-500 hover:text-[#CB141F] text-xs transition-colors"
              data-testid="admin-login-link"
            >
              🔐 Admin Login
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Admin Page Component
const Admin = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    // Check if already authenticated in this session
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAppointments();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem("adminAuth", "true");
      }
    } catch (error) {
      setLoginError("Incorrect password. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setAppointments(sorted);
    } catch (error) {
      toast.error("Failed to load appointments");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) {
      return;
    }
    
    setDeleting(id);
    try {
      await axios.delete(`${API}/appointments/${id}`);
      setAppointments(appointments.filter(apt => apt.id !== id));
      toast.success("Appointment deleted");
    } catch (error) {
      toast.error("Failed to delete appointment");
      console.error(error);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <Toaster position="top-right" richColors />
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#CB141F] mb-2">Fast Cash Tax Services</h1>
            <p className="text-gray-600">Admin Portal</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-[#CB141F] focus:ring-2 focus:ring-[#CB141F]/20 outline-none transition-all"
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loggingIn}
              className="w-full h-12 bg-[#CB141F] hover:bg-[#a01018] text-white font-bold rounded-lg transition-colors disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loggingIn ? "Logging in..." : "Login"}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-gray-500 hover:text-[#CB141F] transition-colors">
              ← Back to Website
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-[#CB141F]">Fast Cash Tax Services</h1>
            <span className="bg-[#CB141F] px-3 py-1 rounded-full text-sm font-bold">ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href="/" 
              className="bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg text-sm transition-colors"
            >
              ← Back to Website
            </a>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm transition-colors"
              data-testid="admin-logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#CB141F] to-[#8B0000] px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Calendar size={24} />
              All Appointments ({appointments.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin w-8 h-8 border-4 border-[#CB141F] border-t-transparent rounded-full mx-auto mb-4"></div>
              Loading appointments...
            </div>
          ) : appointments.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No appointments found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="appointments-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Appointment</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">How Heard</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Booked On</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {appointments.map((apt) => (
                    <tr key={apt.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{apt.name}</div>
                        <div className="text-xs text-gray-500 font-mono">#{apt.id?.slice(0, 8).toUpperCase()}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">{apt.email}</div>
                        <div className="text-sm text-gray-500">{apt.phone}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{apt.service_type}</div>
                        <div className="text-xs text-gray-500">{apt.filing_method}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-[#CB141F]">{apt.preferred_date}</div>
                        <div className="text-sm text-gray-600">{apt.preferred_time || 'No time selected'}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          apt.how_heard 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {apt.how_heard || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDate(apt.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => deleteAppointment(apt.id)}
                          disabled={deleting === apt.id}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`delete-btn-${apt.id}`}
                        >
                          {deleting === apt.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="bg-[#CB141F]/10 p-3 rounded-full">
                <Calendar className="w-6 h-6 text-[#CB141F]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{appointments.length}</p>
                <p className="text-sm text-gray-500">Total Appointments</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {appointments.filter(a => a.how_heard === 'referral').length}
                </p>
                <p className="text-sm text-gray-500">From Referrals</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {appointments.filter(a => a.how_heard === 'google').length}
                </p>
                <p className="text-sm text-gray-500">From Google</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
